import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { useOffline } from "@/contexts/OfflineContext";
import { ConflictResolutionDialog } from "@/components/offline/ConflictResolutionDialog";
import { OfflineEntry } from "@/lib/offline/db";
import {
  Wifi, WifiOff, RefreshCw, CloudOff, Trash2, AlertTriangle, CheckCircle2,
  Clock, XCircle, GitMerge, CloudUpload, HardDrive, BarChart3,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: "Pending", color: "bg-amber-500/10 text-amber-600 border-amber-500/20", icon: Clock },
  synced: { label: "Synced", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", icon: CheckCircle2 },
  failed: { label: "Failed", color: "bg-destructive/10 text-destructive border-destructive/20", icon: XCircle },
  conflict: { label: "Conflict", color: "bg-purple-500/10 text-purple-600 border-purple-500/20", icon: GitMerge },
};

const PIE_COLORS = ["hsl(var(--primary))", "hsl(142, 71%, 45%)", "hsl(0, 84%, 60%)", "hsl(271, 91%, 65%)"];

const SyncDashboardPage = () => {
  const { isOnline, entries, stats, isSyncing, conflicts, syncAll, retryFailed, clearSynced } = useOffline();
  const [conflictEntry, setConflictEntry] = useState<OfflineEntry | null>(null);

  const pieData = [
    { name: "Pending", value: stats.pending },
    { name: "Synced", value: stats.synced },
    { name: "Failed", value: stats.failed },
    { name: "Conflict", value: stats.conflict },
  ].filter((d) => d.value > 0);

  const syncProgress = stats.total > 0 ? Math.round((stats.synced / stats.total) * 100) : 100;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Sync Dashboard</h1>
          <p className="text-sm text-muted-foreground">Monitor offline data and cloud synchronization</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${isOnline ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20" : "bg-destructive/10 text-destructive border border-destructive/20"}`}>
            {isOnline ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
            {isOnline ? "ONLINE MODE" : "OFFLINE MODE"}
          </div>
          <Button onClick={syncAll} disabled={!isOnline || isSyncing || stats.pending === 0} size="sm">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isSyncing ? "animate-spin" : ""}`} />
            {isSyncing ? "Syncing..." : "Sync Now"}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Pending", value: stats.pending, icon: Clock, color: "text-amber-500" },
          { label: "Synced", value: stats.synced, icon: CheckCircle2, color: "text-emerald-500" },
          { label: "Failed", value: stats.failed, icon: XCircle, color: "text-destructive" },
          { label: "Conflicts", value: stats.conflict, icon: GitMerge, color: "text-purple-500" },
        ].map((kpi) => (
          <Card key={kpi.label} className="border-border/50">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">{kpi.label}</p>
                  <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Progress + Pie */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CloudUpload className="h-4 w-4 text-primary" /> Sync Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{stats.synced} of {stats.total} entries synced</span>
              <span className="font-semibold text-foreground">{syncProgress}%</span>
            </div>
            <Progress value={syncProgress} className="h-3" />
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={retryFailed} disabled={stats.failed === 0 || !isOnline}>
                <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Retry Failed ({stats.failed})
              </Button>
              <Button variant="outline" size="sm" onClick={clearSynced} disabled={stats.synced === 0}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear Synced ({stats.synced})
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" /> Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" stroke="none">
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number, name: string) => [`${v} entries`, name]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                <HardDrive className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No offline entries</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Entries Table */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
          <TabsTrigger value="failed">Failed ({stats.failed})</TabsTrigger>
          <TabsTrigger value="conflict">Conflicts ({stats.conflict})</TabsTrigger>
        </TabsList>

        {["all", "pending", "failed", "conflict"].map((tab) => (
          <TabsContent key={tab} value={tab}>
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {tab === "all" ? "All Offline Entries" : `${tab.charAt(0).toUpperCase() + tab.slice(1)} Entries`}
                </CardTitle>
                <CardDescription>
                  {tab === "conflict" ? "Resolve conflicts to complete synchronization" : "Entries stored locally on this device"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries
                      .filter((e) => tab === "all" || e.status === tab)
                      .map((entry) => {
                        const cfg = STATUS_CONFIG[entry.status];
                        const Icon = cfg.icon;
                        return (
                          <TableRow key={entry.id}>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{entry.type}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                                <Icon className="h-3 w-3" />
                                {cfg.label}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{formatDate(entry.createdAt)}</TableCell>
                            <TableCell className="text-sm">{entry.syncAttempts}</TableCell>
                            <TableCell className="text-right">
                              {entry.status === "conflict" && (
                                <Button size="sm" variant="outline" onClick={() => setConflictEntry(entry)}>
                                  <GitMerge className="h-3.5 w-3.5 mr-1" /> Resolve
                                </Button>
                              )}
                              {entry.status === "failed" && entry.lastSyncError && (
                                <span className="text-xs text-destructive">{entry.lastSyncError}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {entries.filter((e) => tab === "all" || e.status === tab).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          <CloudOff className="h-8 w-8 mx-auto mb-2 opacity-40" />
                          No {tab === "all" ? "" : tab} entries found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <ConflictResolutionDialog entry={conflictEntry} open={!!conflictEntry} onClose={() => setConflictEntry(null)} />
    </div>
  );
};

export default SyncDashboardPage;
